import { MerchantUserRole } from 'APIs/merchant.users/merchant.users.types';
import Message from 'Components/Message';
import Modal from 'Components/Modal/Modal';
import { merchantUserRoles } from 'Constants/Constants';
import { FormItem, H5, Label } from 'Constants/styles';
import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

import { yupResolver } from '@hookform/resolvers/yup';

import { FormCheckbox, FormControl, FormWrapper, VInput } from './styles';
import { UserFormProps, defaultUserRole } from './types';

type Props = {
  userFormProps: UserFormProps | null;
  saveMerchantHendler: (formData: UserFormProps) => void;
  modalErrorMsg: string;
  isModalLoading: boolean;
  setShowModal: (showModal: boolean) => void;
};

const UserModal = ({
  userFormProps,
  saveMerchantHendler,
  modalErrorMsg,
  isModalLoading,
  setShowModal,
}: Props): ReactElement => {
  const [isNewUser, setIsNewUser] = useState<boolean>(true);
  const [hideError, setHideError] = useState<boolean>(false);

  const validationSchema = Yup.object().shape({
    email: Yup.string().trim().email('Must be a valid email').required('Email is required'),
    role: Yup.string().oneOf(
      merchantUserRoles.map((b) => b.value as MerchantUserRole),
      'Role is required',
    ),
  });

  const defaultValues: UserFormProps = {
    userId: null,
    email: '',
    role: defaultUserRole,
    enabled: true,
    sendEmail: true,
  };

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
  } = useForm<UserFormProps>({
    resolver: yupResolver(validationSchema),
    defaultValues: userFormProps ?? defaultValues,
  });

  const onSubmit = useCallback(
    async (formData) => {
      saveMerchantHendler(formData);
    },
    [saveMerchantHendler],
  );

  const handleFormSubmit = useCallback(() => {
    return handleSubmit(onSubmit);
  }, [handleSubmit, onSubmit]);

  const handleCancel = () => {
    setShowModal(false);
  };

  useEffect(() => {
    setIsNewUser(!(userFormProps && userFormProps.userId));
  }, [userFormProps]);

  useEffect(() => {
    if (modalErrorMsg) {
      setHideError(false);
    }
  }, [modalErrorMsg]);

  return (
    <Modal
      title={isNewUser ? 'Add new user' : 'Edit user'}
      cancelBtnText="Cancel"
      confirmBtnText={isNewUser ? 'Add' : 'Save'}
      handleCancel={handleCancel}
      handleSubmit={handleFormSubmit()}
      disableConfirmBtn={isModalLoading}
      isLoading={isModalLoading}
      large={false}
      testId={'merchant-user-modal'}
    >
      {modalErrorMsg.length > 0 && !hideError && (
        <Message
          className="mt-2 mb-0"
          success={false}
          description={modalErrorMsg}
          handleClose={(e) => {
            e.preventDefault();
            setHideError(true);
          }}
        />
      )}
      <FormWrapper>
        {isNewUser ? (
          <FormItem>
            <Label>Email address</Label>
            <VInput {...register('email')} isValid={!errors.email} />
            {errors.email && <span className="error-msg">{errors.email.message}</span>}
          </FormItem>
        ) : (
          <FormItem>
            <H5>{getValues('email')}</H5>
          </FormItem>
        )}

        <FormItem>
          <Label>Role</Label>
          <FormControl {...register('role')}>
            {[...merchantUserRoles].map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </FormControl>
          {errors.role && <span className="error-msg">{errors.role.message}</span>}
        </FormItem>
        {isNewUser ? (
          <FormItem>
            <Label>Send onboarding email</Label>
            <FormCheckbox type="checkbox" {...register('sendEmail')} />
            {errors.sendEmail && <span className="error-msg">{errors.sendEmail.message}</span>}
          </FormItem>
        ) : (
          <FormItem>
            <Label>Enabled</Label>
            <FormCheckbox type="checkbox" {...register('enabled')} />
            {errors.enabled && <span className="error-msg">{errors.enabled.message}</span>}
          </FormItem>
        )}
      </FormWrapper>
    </Modal>
  );
};

export default UserModal;
